import Control from '@/Controls/Control';
import ComponentManager from '@/Management/ComponentManager';
import TabStripTabViewModelType from '@/ViewModels/Controls/TabStripTabViewModel';
import LabelType from '@/Controls/Label';
import Factory from '@/Factories/Factory';
import LayoutRenderer from '@/Utility/LayoutRenderer';
import * as CoreFrameDTOs from "coreframetypings/dto";
import ControlFactory from '@/Factories/ControlFactory';
import GroupFactory from '@/Factories/GroupFactory';
import ControlFactoryTuple from '@/Factories/ControlFactoryTuple';
import GroupFactoryTuple from '@/Factories/GroupFactoryTuple';

export default class TabStripTab extends Control {

    private Label = ComponentManager.getControl<typeof LabelType>('Label');
    private jQueryControl: JQuery;
    private label: LabelType;

    /**
     * @override
     */
    public widget: KnockoutObservable<kendo.ui.TabStrip>;

    public constructor(private viewModel: TabStripTabViewModelType, private element?: JQuery) {
        super(viewModel, element);

        this.onWidgetCreated(this.onWidgetCreatedCallback.bind(this));
    }

    public getControl(): JQuery {
        if (this.jQueryControl != null) {
            return this.jQueryControl;
        }

        this.jQueryControl = this.element || $('<li>');

        this.applyBindings(this.jQueryControl, {
            technicalName: this.viewModel.technicalName,
            locator: this.viewModel.locator,
            kendoTab: {
                enabled: this.viewModel.isEnabled,

                widget: this.widget
            }
        });

        return this.jQueryControl;
    }

    private onWidgetCreatedCallback(widget: kendo.ui.TabStrip): void {
        // Wir lassen die Items und Gruppen (und damit die Tupel) direkt asynchron erstellen, rendern sie aber erst,
        // wenn der Tab aktiv wird. Würden wir die Tupel erst erstellen, wenn der Tab aktiv wird, würde der DVC erst
        // als ready gelten, sobald jeder Tab einmal aktiv war.
        const tuplePromise = this.createItemsAndGroupsAsync();

        const renderDependencies = this.defineComputed(() => ({
            isActive: this.viewModel.isActive(),
            isRendered: this.viewModel.isRendered()
        }));
        this.defineSubscription(renderDependencies, ({ isActive, isRendered }) => {
            if (!isActive || isRendered) {
                return;
            }

            tuplePromise.then(tuples => {
                if (this.isDisposed || this.viewModel?.context?.isDisposed !== false)
                    return;

                tuples.sort((a, b) => a.index - b.index);
                const $layout = this.createLayout(tuples.filter(t => t.tuple != null).map(t => t.tuple));
                this.viewModel.content.append($layout);
                this.viewModel.isRendered(true);
            });
        })(renderDependencies());

        this.applyBindings(this.jQueryControl, {
            controlName: 'tabstriptab',

            loader: this.viewModel.isBusy,

            controlIdent: this.identifier,

            tooltip: this.viewModel.tooltip,

            invalid: this.viewModel.invalidationText,

            visible: this.viewModel.isVisible,

            styles: this.definePureComputed({
                read: () => {
                    let styles = ['default'];

                    return styles;
                }
            }),

            states: this.definePureComputed({
                read: () => {
                    let states = [];

                    if (this.viewModel.isRequired() === true) {
                        states.push('required');
                    }

                    if (this.viewModel.isEnabled() === true) {
                        states.push('enabled');
                    } else {
                        states.push('disabled');
                    }

                    return states;
                }
            })
        });

        if (this.viewModel.onBeforeTabChange != null) {
            widget.bind('select', (event: any) => {
                if (this.viewModel.content.hasClass('k-state-active') && this.viewModel.isWizard === true) {
                    event.preventDefault();

                    let currentIndex = widget.select().index();
                    let selectedTab = $(event.item);
                    let selectedIndex = selectedTab.index();

                    $.when(this.viewModel.onBeforeTabChange()).then((canChangeTab: boolean) => {
                        if (canChangeTab === true) {
                            widget.activateTab(selectedTab);
                        }
                    });
                }
            });
        }

        if (this.viewModel.label != null) {
            this.label = new this.Label(this.viewModel.label);
            let tuple = this.label.getTuple();

            tuple.control.appendTo(this.jQueryControl.find('.k-link'));
        }
    }

    private async createItemsAndGroupsAsync(): Promise<{ tuple: ControlFactoryTuple | GroupFactoryTuple, index: number }[]> {
        const promises: Promise<{ tuple: ControlFactoryTuple | GroupFactoryTuple, index: number }[]>[] = [];

        let i = 0;
        const itemPromise = this.createItemsAsync(this.viewModel.detailViewItems, async item => {
            const index = i++;
            const { tuple } = await this.createDvItemAsync(item);
            if (this.isDisposed)
                return null;

            return { tuple, index };
        });
        promises.push(itemPromise);

        const groupPromise = this.createItemsAsync(this.viewModel.detailViewGroups, async group => {
            const index = i++;
            const { tuple } = await this.createGroupAsync(group);
            if (this.isDisposed)
                return null;

            return { tuple, index };
        });
        promises.push(groupPromise);

        const createdTuples = await Promise.all(promises);
        return createdTuples.filter(t => t != null).flatMap(x => x);
    }

    private async createDvItemAsync(i: CoreFrameDTOs.IDVGItemDTO) {
        return await ControlFactory.createInstanceAsync(i, this.viewModel.context, {
            isSummaryViewMode: this.viewModel.isSummaryViewMode,
            locator: this.viewModel.locator
        });
    }

    private createGroupAsync(g: CoreFrameDTOs.IDetailViewGroupDTO) {
        return GroupFactory.createInstanceAsync(g, this.viewModel.context, {
            isSummaryViewMode: this.viewModel.isSummaryViewMode,
            locator: this.viewModel.locator
        });
    }

    private createLayout(tuples: (ControlFactoryTuple | GroupFactoryTuple)[]) {
        return LayoutRenderer.renderLayout(this.viewModel.layout, tuples, this.viewModel.context.labelColumnWidth)
    }
}

ComponentManager.setControl('TabStripTab', TabStripTab, 'RADPresentation.HTMLClient.DefaultViewExtensions');